import { getType } from "@gadgetinc/mobx-quick-tree";
import { camelCase, isFunction, upperFirst } from "lodash";
import { action } from "mobx";
import React from "react";

export type BindingProps<Node extends object, Value> = {
  node: Node;
  error?: boolean;
} & (
  | {
      property: keyof Node & string;
      setter?: string | ((node: Node, value: Value) => void);
    }
  | {
      getter: (node: Node) => Value;
      setter: (node: Node, value: Value) => void;
      showErrorsOnProperty?: keyof Node & string;
    }
);

/**
 * Hook powering inputs that operate directly on MST nodes.
 * Always takes a `node`, and then can take a `property` string to get, and will set using an action named `set<Property>`.
 * Alternatively, can take a `node` and then a `getter` and `setter` function to do arbitrary getting and setting.
 * */
export const useBinding = <Node extends object, Value>(props: BindingProps<Node, Value>): [Value, (value: Value) => void] => {
  let value: Value;
  if ("getter" in props) {
    value = props.getter(props.node);
  } else {
    value = props.node[props.property] as Value;
  }

  const setValue = React.useMemo(() => {
    const setter = props.setter;
    if (setter && isFunction(setter)) {
      return action((value: Value) => {
        setter(props.node, value);
      });
    } else {
      if (!("property" in props)) {
        throw new Error("Can't use a bound input without a `getter` or a `property` option set");
      }

      const setActionName = setter || "set" + upperFirst(camelCase(props.property));

      if (!(setActionName in props.node)) {
        throw new Error(
          `Can't use a bound input on ${getType(props.node).name} node because the setter "${setActionName}" (for node property ${
            props.property
          }) couldn't be found`
        );
      }

      return action((value: Value) => {
        (props.node as any)[setActionName](value);
      });
    }
  }, [props]);

  return [value, setValue];
};
